import { FC } from 'react';
import { useSelector } from 'react-redux';

import { Tooltip } from 'antd';
import cs from 'classnames';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import { useMobileDesign } from 'hooks/useMobileDesign';

import { Dictionaries, JobseekerAvailabilityStatuses } from 'constants/enums';

import { ReactComponent as BriefcaseIcon } from 'assets/icons/briefcase1.svg';
import { ReactComponent as BuildingIcon } from 'assets/icons/building.svg';
import { ReactComponent as ProfileCircleIcon } from 'assets/icons/profile-circle-filled.svg';
import { ReactComponent as SlashIcon } from 'assets/icons/slash.svg';
import { ReactComponent as TickCircleFilledIcon } from 'assets/icons/tick-circle-filled.svg';

import styles from './AvailabilityStatus.module.scss';

const DEFAULT_AVAILABILITY_STATUS_TEXT = 'Unknown';

const statusClassNames: Record<JobseekerAvailabilityStatuses, string> = {
  [JobseekerAvailabilityStatuses.ReadyToStart]: styles.readyToStart,
  [JobseekerAvailabilityStatuses.Employed]: styles.employed,
  [JobseekerAvailabilityStatuses.ReceivingOffers]: styles.receivingOffers,
  [JobseekerAvailabilityStatuses.Inactive]: styles.inactive
};

const statusIcons: Record<
  JobseekerAvailabilityStatuses,
  React.FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {
  [JobseekerAvailabilityStatuses.ReadyToStart]: TickCircleFilledIcon,
  [JobseekerAvailabilityStatuses.Employed]: BuildingIcon,
  [JobseekerAvailabilityStatuses.ReceivingOffers]: BriefcaseIcon,
  [JobseekerAvailabilityStatuses.Inactive]: ProfileCircleIcon
};

const defaultIcon = SlashIcon;
const defaultClassName = styles.unknown;

interface AvailabilityStatusProps {
  status?: JobseekerAvailabilityStatuses | null;
  tooltipTitle?: string;
}

const AvailabilityStatus: FC<AvailabilityStatusProps> = ({
  status,
  tooltipTitle
}) => {
  const isMobile = useMobileDesign();
  const jobsekerAvailabilityStatusesDict = useSelector(
    selectDictionary(Dictionaries.JobseekerAvailabilityStatus)
  );

  const IconComponent = status
    ? statusIcons[status] || defaultIcon
    : defaultIcon;

  const statusClassName = status
    ? statusClassNames[status] || defaultClassName
    : defaultClassName;

  const statusText = status
    ? jobsekerAvailabilityStatusesDict.find((el) => el.value === status)
        ?.label || DEFAULT_AVAILABILITY_STATUS_TEXT
    : DEFAULT_AVAILABILITY_STATUS_TEXT;

  return (
    <Tooltip title={tooltipTitle} placement={isMobile ? 'top' : 'right'}>
      <div className={cs(styles.statusContainer, statusClassName)}>
        <IconComponent className={styles.statusIcon} />
        {statusText}
      </div>
    </Tooltip>
  );
};

export default AvailabilityStatus;
